import { Field, FieldProps, Form, Formik } from 'formik';

import { IconButton, styled } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import {
  SText,
  SBase,
  SButton,
  SFormikTextfield
} from '@setvi/shared/components';
import { TextColor } from '@setvi/shared/enums';
import { useCustomErrors } from '@setvi/shared/hooks';
import { SlackMutationKeys } from '@setvi/shared/services';

import { useMutationTracker } from 'hooksv2/useMutationTracker';
import { ThumbsDownIcon, ThumbsUpIcon } from '@setvi/shared/components/sicons';
import {
  schema,
  MAX_TEXT,
  ratingValidation,
  feedbackValidation
} from './utils';
import { Content, Footer, Header, RatingItem, Ratings } from './styled';

const StyledSubmitButton = styled(SButton)({
  '&.Mui-disabled': {
    backgroundColor: '#EEF0F2'
  }
});

export interface FeedbackValues {
  feedback: string;
  rating: string | null;
}

interface FeedbackProps {
  handleSubmit: ({ feedback, rating }: FeedbackValues) => void;
  onClose: () => void;
  loading?: boolean;
  label?: string;
  title?: string;
}

export const FEEDBACK_DIALOG = 'feedback-dialog';

const Feedback = ({
  handleSubmit,
  onClose,
  loading = false,
  title = 'We’d love your feedback',
  label = 'How can we improve AI Product Match?'
}: FeedbackProps) => {
  const { isLoading } = useMutationTracker({
    key: SlackMutationKeys.SEND_CHANNEL_MSG
  });

  const { customErrors, customValidate } = useCustomErrors({
    schemas: schema,
    values: {
      feedback: '',
      rating: null
    }
  });

  const ratings = [
    {
      value: 'Good',
      icon: <ThumbsUpIcon fill="inherit" style={{ width: 24 }} />,
      inactive: {
        borderColor: TextColor.Blue,
        backgroundColor: 'transparent',
        fill: TextColor.Blue
      },
      active: {
        borderColor: TextColor.Blue,
        backgroundColor: TextColor.Blue,
        fill: '#fff'
      }
    },
    {
      value: 'Bad',
      icon: <ThumbsDownIcon fill="inherit" style={{ width: 24 }} />,
      inactive: {
        borderColor: TextColor.SlateGrey,
        backgroundColor: 'transparent',
        fill: TextColor.SlateGrey
      },
      active: {
        borderColor: '#20262D',
        backgroundColor: '#20262D',
        fill: '#fff'
      }
    }
  ];

  return (
    <SBase
      width={365}
      maxWidth="100%"
      disablePadding
      style={{ background: '#F8F9FA' }}>
      <Header>
        <SText title={title} size="xl" lineHeight="28px" weight="bold" />
        <IconButton
          aria-label="close"
          size="small"
          onClick={onClose}
          style={{
            padding: 2
          }}>
          <CloseIcon />
        </IconButton>
      </Header>
      <Formik
        initialValues={{
          feedback: '',
          rating: null
        }}
        onSubmit={handleSubmit}>
        {({ values }) => (
          <Form>
            <Content>
              <Field name="rating">
                {({ meta, form }: FieldProps) => (
                  <Ratings>
                    {ratings?.map(r => (
                      <RatingItem
                        key={r?.value}
                        customStyle={
                          meta.value === r?.value ? r?.active : r?.inactive
                        }
                        onClick={() => {
                          form.setFieldValue('rating', r.value);
                          customValidate({
                            schema: ratingValidation,
                            value: r.value,
                            name: 'rating'
                          });
                        }}>
                        {r?.icon}
                      </RatingItem>
                    ))}
                  </Ratings>
                )}
              </Field>

              <SFormikTextfield
                name="feedback"
                maxRows={4}
                label={
                  <SText
                    weight="semibold"
                    size="sm"
                    lineHeight="20px"
                    style={{
                      margin: '8px 0'
                    }}
                    fontColor={TextColor.DarkGrey}
                    title={label}
                  />
                }
                placeholder="Enter Feedback..."
                InputProps={{
                  style: {
                    borderRadius: 12,
                    background: '#fff'
                  }
                }}
                required
                multiline
                maxLength={MAX_TEXT}
                onChange={(value: string) =>
                  customValidate({
                    schema: feedbackValidation,
                    value,
                    name: 'feedback'
                  })
                }
                customErrors={customErrors}
              />
            </Content>
            <Footer>
              <SButton
                fullWidth
                color="default"
                variant="outlined"
                style={{ borderRadius: 120, color: TextColor.SlateGrey }}
                onClick={onClose}
                disabled={loading || isLoading}>
                Cancel
              </SButton>
              <StyledSubmitButton
                fullWidth
                type="submit"
                style={{ borderRadius: 120 }}
                loading={loading || isLoading}
                disabled={
                  // @ts-ignore
                  customErrors?.feedback?.length > 0 ||
                  !values?.feedback?.trim() ||
                  loading ||
                  isLoading
                }>
                Send Feedback
              </StyledSubmitButton>
            </Footer>
          </Form>
        )}
      </Formik>
    </SBase>
  );
};

export default Feedback;
